import React from 'react';
import { RecordActionsMenu } from '@/shared/components/RecordActionsMenu';
import { Badge } from '@/shared/ui/badge';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import type { SubmittalPriority, SubmittalStatus, SubmittalSummary } from '../types';
import { SUBMITTAL_PRIORITY_LABELS, SUBMITTAL_STATUS_LABELS, SUBMITTAL_TYPE_LABELS } from '../types';
import { formatDate } from '../utils/format';

// Solid, filled status pills.
const STATUS_BADGE: Record<SubmittalStatus, string> = {
  DRAFT: 'border-transparent bg-gray-500 text-white hover:bg-gray-500',
  OPEN: 'border-transparent bg-blue-600 text-white hover:bg-blue-600',
  IN_REVIEW: 'border-transparent bg-amber-500 text-white hover:bg-amber-500',
  APPROVED: 'border-transparent bg-green-600 text-white hover:bg-green-600',
  APPROVED_WITH_COMMENTS: 'border-transparent bg-emerald-500 text-white hover:bg-emerald-500',
  REVISE_RESUBMIT: 'border-transparent bg-orange-500 text-white hover:bg-orange-500',
  REJECTED: 'border-transparent bg-red-600 text-white hover:bg-red-600',
  CLOSED: 'border-transparent bg-slate-600 text-white hover:bg-slate-600',
};

// Lighter tinted priority pills so status stays the dominant signal.
const PRIORITY_BADGE: Record<SubmittalPriority, string> = {
  LOW: 'border-transparent bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  MEDIUM: 'border-transparent bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100',
  HIGH: 'border-transparent bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100',
  CRITICAL: 'border-transparent bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100',
};

const COLUMN_COUNT = 13;

interface Props {
  items: SubmittalSummary[];
  isLoading: boolean;
  error?: unknown;
  onRowClick: (item: SubmittalSummary) => void;
  onEdit: (item: SubmittalSummary) => void;
  onDelete: (item: SubmittalSummary) => void;
}

export const SubmittalsTable: React.FC<Props> = ({
  items,
  isLoading,
  error,
  onRowClick,
  onEdit,
  onDelete,
}) => {
  const renderSkeletonRows = () =>
    Array.from({ length: 5 }).map((_, i) => (
      <TableRow key={i}>
        {Array.from({ length: COLUMN_COUNT }).map((__, j) => (
          <TableCell key={j} className="py-2">
            <Skeleton className="h-4 w-full" />
          </TableCell>
        ))}
      </TableRow>
    ));

  return (
    <div className="min-w-0 space-y-2" data-tour-id="submittals-table">
      <div className="space-y-2 md:hidden">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-72 w-full rounded-md" />
          ))
        ) : error ? (
          <div className="rounded-md border px-4 py-8 text-center text-sm text-destructive">
            Failed to load submittals.
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-md border px-4 py-10 text-center text-sm text-muted-foreground">
            No submittals found.
          </div>
        ) : (
          items.map((item) => (
            <div
              key={item.id}
              className="w-full min-w-0 rounded-md border bg-card p-4 text-left transition-colors hover:bg-muted/50"
              onClick={() => onRowClick(item)}
            >
              <div className="flex min-w-0 items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                    <span className="font-mono">{item.submittal_number}</span>
                    <span>Rev {item.revision_number}</span>
                  </div>
                  <p className="mt-1 break-words text-sm font-semibold">{item.title}</p>
                </div>
                <RecordActionsMenu
                  label={`submittal ${item.submittal_number}`}
                  canEdit={item.can_edit}
                  canDelete={item.can_delete}
                  onView={() => onRowClick(item)}
                  onEdit={() => onEdit(item)}
                  onDelete={() => onDelete(item)}
                />
              </div>
              <div className="mt-4 grid min-w-0 grid-cols-2 gap-x-4 gap-y-3">
                <MobileCardField label="Status">
                  <Badge className={`max-w-full whitespace-normal text-left text-xs ${STATUS_BADGE[item.status]}`}>
                    {SUBMITTAL_STATUS_LABELS[item.status]}
                  </Badge>
                </MobileCardField>
                <MobileCardField label="Priority">
                  <Badge className={`max-w-full whitespace-normal text-left text-xs ${PRIORITY_BADGE[item.priority]}`}>
                    {SUBMITTAL_PRIORITY_LABELS[item.priority]}
                  </Badge>
                </MobileCardField>
                <MobileCardField label="Current Reviewer">
                  {item.current_reviewer
                    ? `${item.current_reviewer.first_name} ${item.current_reviewer.last_name}`
                    : 'No reviewer'}
                </MobileCardField>
                <MobileCardField label="Due Date">
                  <span
                    className={
                    item.is_overdue ? 'font-semibold text-red-600' : ''
                    }
                  >
                    {formatDate(item.required_response_date)}
                  </span>
                </MobileCardField>
                <MobileCardField label="Package">
                  {item.package?.package_id || 'Unpackaged'}
                </MobileCardField>
                <MobileCardField label="Type">
                  {SUBMITTAL_TYPE_LABELS[item.submittal_type]}
                </MobileCardField>
                <MobileCardField label="Contractor">
                  {item.responsible_contractor || '—'}
                </MobileCardField>
                <MobileCardField label="Spec Section">
                  {item.specification_section || '—'}
                </MobileCardField>
                <MobileCardField label="Days Open">
                  {item.days_open} day{item.days_open === 1 ? '' : 's'} open
                </MobileCardField>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="hidden overflow-x-auto rounded-md border md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs font-medium whitespace-nowrap">ID</TableHead>
              <TableHead className="text-xs font-medium">Package</TableHead>
              <TableHead className="text-xs font-medium">Title</TableHead>
              <TableHead className="text-xs font-medium">Type</TableHead>
              <TableHead className="text-xs font-medium">Spec Section</TableHead>
              <TableHead className="text-xs font-medium">Contractor</TableHead>
              <TableHead className="text-xs font-medium">Current Reviewer</TableHead>
              <TableHead className="text-xs font-medium whitespace-nowrap">Due Date</TableHead>
              <TableHead className="text-xs font-medium w-[150px]">Status</TableHead>
              <TableHead className="text-xs font-medium whitespace-nowrap">Revision</TableHead>
              <TableHead className="text-xs font-medium whitespace-nowrap">Days Open</TableHead>
              <TableHead className="text-xs font-medium w-[100px]">Priority</TableHead>
              <TableHead className="w-[72px] text-xs font-medium">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              renderSkeletonRows()
            ) : error ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="py-8 text-center text-destructive">
                  Failed to load submittals.
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="py-12 text-center text-sm text-muted-foreground">
                  No submittals found.
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <SubmittalRow
                  key={item.id}
                  item={item}
                  onView={onRowClick}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

function MobileCardField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {label}
      </p>
      <div className="mt-1 min-w-0 break-words text-xs font-medium text-foreground">
        {children}
      </div>
    </div>
  );
}

function SubmittalRow({
  item,
  onView,
  onEdit,
  onDelete,
}: {
  item: SubmittalSummary;
  onView: (item: SubmittalSummary) => void;
  onEdit: (item: SubmittalSummary) => void;
  onDelete: (item: SubmittalSummary) => void;
}) {
  return (
    <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => onView(item)}>
      <TableCell className="py-1.5 text-xs font-mono text-muted-foreground whitespace-nowrap">
        {item.submittal_number}
      </TableCell>
      <TableCell className="py-1.5 text-xs">{item.package?.package_id || 'Unpackaged'}</TableCell>
      <TableCell className="max-w-[220px] truncate py-1.5 text-xs font-medium">{item.title}</TableCell>
      <TableCell className="py-1.5 text-xs">{SUBMITTAL_TYPE_LABELS[item.submittal_type]}</TableCell>
      <TableCell className="py-1.5 text-xs text-muted-foreground">
        {item.specification_section || '—'}
      </TableCell>
      <TableCell className="py-1.5 text-xs text-muted-foreground">
        {item.responsible_contractor || '—'}
      </TableCell>
      <TableCell className="py-1.5 text-xs text-muted-foreground">
        {item.current_reviewer
          ? `${item.current_reviewer.first_name} ${item.current_reviewer.last_name}`
          : '—'}
      </TableCell>
      <TableCell
        className={`py-1.5 text-xs whitespace-nowrap ${
          item.is_overdue ? 'font-semibold text-red-600' : 'text-muted-foreground'
        }`}
      >
        {formatDate(item.required_response_date)}
      </TableCell>
      <TableCell className="py-1.5">
        <Badge className={`text-xs whitespace-nowrap ${STATUS_BADGE[item.status]}`}>
          {SUBMITTAL_STATUS_LABELS[item.status]}
        </Badge>
      </TableCell>
      <TableCell className="py-1.5 text-xs font-mono whitespace-nowrap">
        Rev {item.revision_number}
      </TableCell>
      <TableCell className="py-1.5 text-xs tabular-nums">{item.days_open}</TableCell>
      <TableCell className="py-1.5">
        <Badge className={`text-xs whitespace-nowrap ${PRIORITY_BADGE[item.priority]}`}>
          {SUBMITTAL_PRIORITY_LABELS[item.priority]}
        </Badge>
      </TableCell>
      <TableCell className="py-1.5">
        <RecordActionsMenu
          label={`submittal ${item.submittal_number}`}
          canEdit={item.can_edit}
          canDelete={item.can_delete}
          onView={() => onView(item)}
          onEdit={() => onEdit(item)}
          onDelete={() => onDelete(item)}
        />
      </TableCell>
    </TableRow>
  );
}

export default SubmittalsTable;
